import React, { ReactElement, RefCallback, RefObject } from 'react';
import camelcase from 'camelcase-es5';

import Icon from '../Icon/Icon';
import Box from '../Box/Box';
import Text from '../Text';
import { InfoBadge } from '../../components/Badge';
import Asset from '../../components/Asset';

type TSelectionInput = {
  /** value of the checkbox dom input*/
  value: string;
  /** The id of the checkbox input, also used on the label as htmlFor */
  id?: string;
  /** Whether it is checked */
  isChecked: boolean;
  /** The label text. Also passed into aria-label attribute on the input itself*/
  labelText: string;
  ariaLabel?: string;
  // ** Count of items in a formatted string */
  counter?: string;
  /** Only visible on mobile + stacked */
  icon?: string;
  /** Only visible on mobile + stacked */
  helperText?: string;
  disabled?: boolean;
  /** On component change */
  onChange?: React.FormEventHandler<HTMLInputElement>;
  /** Component name */
  componentName: string;
  CheckMark?: () => ReactElement;
  IndeterminateIcon?: () => ReactElement;
  inputRef?: RefObject<HTMLInputElement> | RefCallback<HTMLInputElement>;
  name?: string;
  isIndeterminate?: boolean;
  tabIndex?: number;
  badgeLabel: string;
  assetName?: string;
} & React.HTMLAttributes<any>;

type TSelectionInputBody = {
  icon?: string;
  labelText: string;
  counter?: string;
  helperText?: string;
  badgeLabel?: string;
  assetName?: string;
};

type TInput = {
  ariaLabel?: string;
  helperText?: string;
  isChecked: boolean;
  id: string;
  onChange?: React.FormEventHandler<HTMLInputElement>;
  componentName: string;
  value: string;
  inputRef?: RefObject<HTMLInputElement> | RefCallback<HTMLInputElement>;
  disabled?: boolean;
  htmlNameAttr?: string;
  tabIndex?: number;
} & React.InputHTMLAttributes<any>;

// eslint-disable-next-line react/display-name
const Input = ({
  value,
  isChecked,
  onChange,
  ariaLabel,
  id,
  componentName,
  inputRef,
  disabled,
  htmlNameAttr,
  tabIndex,
}: TInput) => (
  <Box
    parent="SelectionInput"
    name="Input"
    as="input"
    type={componentName}
    id={id}
    ref={inputRef}
    htmlNameAttr={htmlNameAttr ? htmlNameAttr : id}
    checked={isChecked}
    value={value}
    onChange={onChange}
    aria-label={ariaLabel}
    disabled={disabled}
    tabIndex={tabIndex}
  />
);

const SelectionInputBody = ({
  labelText,
  counter,
  helperText,
  icon,
  badgeLabel,
  assetName,
}: TSelectionInputBody): ReactElement => (
  <Box as="div" parent="SelectionInput" name="Container">
    {icon && (
      <Box as="div" parent="SelectionInput" name="Icon">
        <Icon name={icon} color="text-primary" />
      </Box>
    )}
    {assetName && (
      <Box as="div" parent="SelectionInput" name="Asset">
        <Asset.Square name={assetName} size="l" />
      </Box>
    )}
    <Box as="div" parent="SelectionInput" name="Body">
      <Box as="div" parent="SelectionInput" name="Label">
        {labelText && (
          <Box as="span" parent="SelectionInput" name="LabelText">
            <Text.Large color="text-primary">{labelText}</Text.Large>
          </Box>
        )}
        {counter && (
          <Box as="span" parent="SelectionInput" name="Counter">
            <Text.Regular color="text-secondary">{counter}</Text.Regular>
          </Box>
        )}
        {Boolean(badgeLabel) && <InfoBadge utilities={['marginLeftXxxs']}>{badgeLabel}</InfoBadge>}
      </Box>
      {helperText && (
        <Box as="span" parent="SelectionInput" name="Helper">
          <Text.Regular color="text-secondary">{helperText}</Text.Regular>
        </Box>
      )}
    </Box>
  </Box>
);

const SelectionInput = ({
  value,
  isChecked,
  isIndeterminate,
  labelText,
  ariaLabel,
  counter,
  icon,
  helperText,
  onChange,
  componentName,
  CheckMark,
  IndeterminateIcon,
  inputRef,
  disabled,
  id = `${componentName}-${camelcase(labelText)}-${camelcase(value)}`,
  name,
  tabIndex,
  badgeLabel,
  assetName,
  ...rest
}: TSelectionInput): ReactElement => (
  <Box as="label" name="SelectionInput" modifiers={componentName} htmlFor={id} {...rest}>
    <Box as="div" parent="SelectionInput" name="InputContainer">
      <Input
        value={value}
        isChecked={isChecked}
        id={id}
        onChange={onChange}
        ariaLabel={ariaLabel}
        componentName={componentName}
        inputRef={inputRef}
        disabled={disabled}
        htmlNameAttr={name}
        tabIndex={tabIndex}
      />
      {CheckMark && isChecked && <CheckMark />}
      {!isChecked && isIndeterminate && IndeterminateIcon && <IndeterminateIcon />}
    </Box>
    <SelectionInputBody
      icon={icon}
      counter={counter}
      labelText={labelText}
      helperText={helperText}
      badgeLabel={badgeLabel}
      assetName={assetName}
    />
  </Box>
);

export default SelectionInput;
